import { Menu, Transition } from '@headlessui/react';

// New constant for the loading state component
const LoadingState = () => (
  <div className="flex items-center space-x-2">
    <svg
      className="animate-spin h-5 w-5 text-slate-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
    <span>Loading...</span>
  </div>
);

export default function DropdownFull({
  options,
  selected,
  handleChange,
  title,
  dataCy,
  disabled = false,
  isLoading = false,
}: {
  options: Array<{ id: string; value: string, disabled?: boolean }>;
  selected: { id: string; value: string };
  handleChange: (id: string) => void;
  title?: string;
  dataCy?: string;
  disabled?: boolean;
  isLoading?: boolean;
}): JSX.Element {
  return (
    <Menu as="div" className="relative inline-flex" data-cy={dataCy}>
      {({ open }) => (
        <>
          <Menu.Button
            className={`btn w-full justify-between min-w-[12rem] bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-500 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-200 ${
              disabled || isLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
            }`}
            aria-label="Select option"
            data-cy="dropdown-button"
            disabled={disabled || isLoading}
            aria-disabled={disabled || isLoading}
          >
            <span className="flex items-center">
              {/* The conditional logic is now much cleaner */}
              {isLoading ? <LoadingState /> : <span>{title || selected.value}</span>}
            </span>
            <svg
              className="shrink-0 ml-1 fill-current text-slate-400"
              width="11"
              height="7"
              viewBox="0 0 11 7"
            >
              <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
            </svg>
          </Menu.Button>
          <Transition
            className="z-10 absolute top-full left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1"
            enter="transition ease-out duration-100 transform"
            enterFrom="opacity-0 -translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-out duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Menu.Items className="font-medium text-sm text-slate-600 dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700 focus:outline-none">
              {options.map((option, optionIndex) => (
                <Menu.Item key={optionIndex}>
                  {({ active }) => (
                    <button
                      className={`flex items-center justify-between w-full py-2 px-3 cursor-pointer ${
                        active ? 'bg-slate-50 dark:bg-slate-700/20' : ''
                      }
                      ${disabled || option.disabled === true ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}
                      ${option.id === selected.id && 'text-indigo-500'}`}
                      onClick={() => {
                        handleChange(option.id);
                      }}
                      disabled={disabled || option.disabled}
                    >
                      <span>{option.value}</span>
                      <svg
                        className={`shrink-0 mr-2 fill-current text-indigo-500 ${
                          option.id !== selected.id && 'invisible'
                        }`}
                        width="12"
                        height="9"
                        viewBox="0 0 12 9"
                      >
                        <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                      </svg>
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}
